/**
 * Authentication Hooks
 * React hooks for auth state and protected routes
 */

import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores';
import { supabase } from '@/lib/supabase-client';
import { queryKeys } from '@/lib/api-client';
import type { User, LoginCredentials, RegisterCredentials } from '@/types';

// ============================================
// useAuth Hook
// ============================================

export function useAuth() {
  const { user, session, isLoading, isInitialized, error, login, logout, refreshSession, clearError } = useAuthStore();

  return {
    user,
    session,
    isLoading,
    isInitialized,
    isAuthenticated: !!session,
    error,
    login,
    logout,
    refreshSession,
    clearError,
  };
}

// ============================================
// useRequireAuth Hook
// ============================================

interface UseRequireAuthOptions {
  redirectTo?: string;
}

export function useRequireAuth(options: UseRequireAuthOptions = {}) {
  const { redirectTo = '/login' } = options;
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isInitialized, isLoading } = useAuth();
  const [isRedirecting, setIsRedirecting] = useState(false);

  useEffect(() => {
    if (isInitialized && !isAuthenticated && !isLoading && !isRedirecting) {
      setIsRedirecting(true);
      // Save the current location to redirect back after login
      const returnUrl = encodeURIComponent(location.pathname + location.search);
      navigate(`${redirectTo}?returnUrl=${returnUrl}`, { replace: true });
    }
  }, [isAuthenticated, isInitialized, isLoading, navigate, redirectTo, location, isRedirecting]);

  // Return null state when redirecting to prevent rendering
  if (isRedirecting) {
    return { isAuthenticated: false, isInitialized: false, isLoading: false };
  }

  return { isAuthenticated, isInitialized, isLoading };
}

// ============================================
// useLogin Mutation
// ============================================

export function useLogin() {
  const queryClient = useQueryClient();
  const { login } = useAuthStore();

  return useMutation({
    mutationFn: async (credentials: LoginCredentials) => {
      await login(credentials.email, credentials.password);
    },
    onSuccess: () => {
      // Invalidate and refetch auth-related queries
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.me });
    },
  });
}

// ============================================
// useLogout Mutation
// ============================================

export function useLogout() {
  const queryClient = useQueryClient();
  const { logout } = useAuthStore();

  return useMutation({
    mutationFn: async () => {
      await logout();
    },
    onSuccess: () => {
      // Clear all queries from cache on logout
      queryClient.clear();
    },
  });
}

// ============================================
// useCurrentUser Query
// ============================================

export function useCurrentUser() {
  return useQuery({
    queryKey: queryKeys.auth.me,
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      return user as unknown as User;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: false,
  });
}

// ============================================
// useRegister / useSignup Mutation
// ============================================

export function useRegister() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (credentials: RegisterCredentials) => {
      const { data, error } = await supabase.auth.signUp({
        email: credentials.email,
        password: credentials.password,
        options: {
          data: {
            name: credentials.name,
          },
        },
      });

      if (error) {
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.me });
    },
  });
}

// Alias for useRegister - useSignup is more intuitive for signup flows
export const useSignup = useRegister;

// ============================================
// useAuthCheck Hook
// ============================================

export function useAuthCheck() {
  const { isInitialized, initialize } = useAuthStore();

  useEffect(() => {
    if (!isInitialized) {
      initialize();
    }
  }, [isInitialized, initialize]);

  return { isInitialized };
}

// ============================================
// usePasswordReset Mutation
// ============================================

export function usePasswordReset() {
  return useMutation({
    mutationFn: async (email: string) => {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        throw error;
      }
    },
  });
}

// ============================================
// useUpdatePassword Mutation
// ============================================

export function useUpdatePassword() {
  return useMutation({
    mutationFn: async (newPassword: string) => {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) {
        throw error;
      }
    },
  });
}
